<div class="pickup-scans-container">
  <!-- Filters Section -->
  <div class="filters-section">
    <div class="filter-row">
      <!-- Date Range Picker (max 7 days, no future dates) -->
      <mat-form-field appearance="outline" class="date-range-field">
        <mat-label>Date range</mat-label>
        <mat-date-range-input [formGroup]="range" [rangePicker]="picker" [max]="maxDate">
          <input matStartDate formControlName="start" placeholder="Start date" (dateChange)="onStartDateChange($event)" />
          <input matEndDate formControlName="end" placeholder="End date" />
        </mat-date-range-input>
        <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
        <mat-date-range-picker #picker (opened)="onPickerOpened()"></mat-date-range-picker>
      </mat-form-field>

      <!-- Provider Filter -->
      <mat-form-field appearance="outline" class="provider-filter">
        <mat-label>Pickup provider: {{ selectedProviders.length === 0 ? 'All' : '' }}</mat-label>
        <mat-select [(value)]="selectedProviders" multiple>
          <mat-option *ngFor="let provider of pickupProviders" [value]="provider.id">
            {{ provider.name }}
          </mat-option>
        </mat-select>
      </mat-form-field>

      <!-- Location Filter -->
      <mat-form-field appearance="outline" class="location-filter">
        <mat-label>Airport: {{ selectedLocations.length === 0 ? 'All' : '' }}</mat-label>
        <mat-select [(value)]="selectedLocations" multiple>
          <mat-option *ngFor="let locationCode of locationOptions" [value]="locationCode">
            {{ locationCode }}
          </mat-option>
        </mat-select>
      </mat-form-field>

      <!-- Search Button -->
      <button mat-flat-button class='align-right' color='primary' type='submit' (click)='loadData()'>Search</button>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="loading" class='align-center mt--lg'>
    <app-loading></app-loading>
  </div>

  <!-- Error State -->
  <div *ngIf="hasError && !loading" class="error-container">
    <mat-icon class="error-icon">error_outline</mat-icon>
    <span class="error-text">{{ errorMessage }}</span>
    <button mat-stroked-button color="primary" (click)="loadData()">Retry</button>
  </div>

  <!-- Data Display -->
  <div *ngIf="!loading && !hasError && responseData" class="data-section">
    <!-- Summary Cards -->
    <div class="summary-cards">
      <div class="summary-card">
        <div class="card-icon green">
          <mat-icon class='material-symbols-outlined'>unarchive</mat-icon>
        </div>
        <div class="card-content">
          <div class="card-value">{{ formatNumber(responseData.totalPickupScans) }}</div>
          <div class="card-label">Pickups</div>
        </div>
      </div>
      <div class="summary-card">
        <div class="card-icon yellow">
          <mat-icon class='material-symbols-outlined'>barcode_reader</mat-icon>
        </div>
        <div class="card-content">
          <div class="card-value">{{ formatNumber(responseData.totalScans) }}</div>
          <div class="card-label">Total Scanned</div>
        </div>
      </div>
      <div *ngIf="!isAppleExpressUser" class="summary-card">
        <div class="card-icon purple">
          <mat-icon class='material-symbols-outlined'>request_quote</mat-icon>
        </div>
        <div class="card-content">
          <div class="card-value">${{ formatNumber(responseData.totalCost) }}</div>
          <div class="card-label">Total Cost</div>
        </div>
      </div>
    </div>

    <!-- Data Table -->
    <div class="table-container" *ngIf="responseData.data.length > 0">
      <table mat-table [dataSource]="responseData.data" class="pickup-scans-table">
        <!-- Company Column -->
        <ng-container matColumnDef="company">
          <th mat-header-cell *matHeaderCellDef class="company-header">Customer / Date</th>
          <td mat-cell *matCellDef="let company">
            <div class="company-cell">
              <span class="company-name">{{ company.companyName }}</span>
            </div>
          </td>
        </ng-container>

        <!-- Dynamic Date Columns -->
        <ng-container *ngFor="let dateCol of dateColumns; let i = index" [matColumnDef]="'date_' + i">
          <th mat-header-cell *matHeaderCellDef class="date-header">{{ getFormattedDateHeader(i) }}</th>
          <td mat-cell *matCellDef="let company" class="date-cell">
            <div class="cell-values">
              <span class="count-value">{{ getTotalScansPerDay(company, i) }}</span>
              <span *ngIf="!isAppleExpressUser" class="cost-value">${{ getPickupCost(company, i) }}</span>
            </div>
          </td>
        </ng-container>

        <!-- Total Column -->
        <ng-container matColumnDef="total">
          <th mat-header-cell *matHeaderCellDef class="total-header">Total</th>
          <td mat-cell *matCellDef="let company" class="total-cell">
            <div class="cell-values">
              <span class="count-value">{{ formatNumber(getCompanyTotalScans(company)) }}</span>
              <span *ngIf="!isAppleExpressUser" class="cost-value">${{ getCompanyTotalCost(company) }}</span>
            </div>
          </td>
        </ng-container>

        <!-- Footer: Company Column -->
        <ng-container matColumnDef="company-footer">
          <td mat-footer-cell *matFooterCellDef class="footer-label">Daily total</td>
        </ng-container>

        <!-- Footer: Dynamic Date Columns -->
        <ng-container *ngFor="let dateCol of dateColumns; let i = index" [matColumnDef]="'date_' + i + '_footer'">
          <td mat-footer-cell *matFooterCellDef class="footer-cell">
            <div class="cell-values">
              <span class="count-value">{{ formatNumber(getDailyTotal(i)) }}</span>
              <span *ngIf="!isAppleExpressUser" class="cost-value">${{ getDailyTotalCost(i) }}</span>
            </div>
          </td>
        </ng-container>

        <!-- Footer: Total Column -->
        <ng-container matColumnDef="total_footer">
          <td mat-footer-cell *matFooterCellDef class="total-footer-cell">
            <div class="cell-values">
              <span class="count-value">{{ formatNumber(getGrandTotalPickups()) }}</span>
              <span *ngIf="!isAppleExpressUser" class="cost-value">${{ getGrandTotalPickupsCost() }}</span>
            </div>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
        <tr mat-footer-row *matFooterRowDef="footerColumns" class="footer-row"></tr>
      </table>
    </div>

    <!-- Empty State -->
    <div *ngIf="responseData.data.length === 0" class="empty-state">
      <mat-icon>inbox</mat-icon>
      <span>No pickup scan data found for the selected filters.</span>
    </div>

    <!-- Footer: Export and Pagination -->
    <div class="table-footer" *ngIf="responseData.pagination.totalItems > 0 && canExportCsv">
      <button mat-flat-button color='primary' type='submit' [disabled]="exporting || !responseData || responseData.data.length === 0" (click)='exportToCsv()'>
        <app-loading [inButton]='true' *ngIf='exporting'></app-loading>
        <span *ngIf='!exporting'>Export Data</span>
      </button>
      <div class="pagination-wrapper">
        <mat-paginator
          [length]="responseData.pagination.totalItems"
          [pageSize]="responseData.pagination.limit"
          [pageIndex]="responseData.pagination.page - 1"
          [pageSizeOptions]="[10, 20, 50, 100]"
          (page)="onPageChange($event)"
          showFirstLastButtons>
        </mat-paginator>
      </div>
    </div>
  </div>
</div>
